import type { TypedSupabaseClient } from '../client';
import { chunkIds, fetchAllRows } from './fetch-all-rows';

interface LowStockArticleRow {
  id: string;
  code: string;
  name: string;
  stock_min: number | null;
  unit: string | null;
  tenant_id: string;
}

interface StockMovementRow {
  article_id: string;
  movement_type: string;
  quantity: number;
}

type RpcClient = {
  rpc: (
    fn: string,
    args: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: unknown }>;
};

function formatQty(value: number): string {
  return value.toLocaleString('fr-TN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

function aggregateStock(rows: StockMovementRow[]): Map<string, number> {
  const stock = new Map<string, number>();

  for (const row of rows) {
    const current = stock.get(row.article_id) ?? 0;
    const quantity = Number(row.quantity ?? 0);
    if (row.movement_type === 'in') {
      stock.set(row.article_id, current + quantity);
    } else if (row.movement_type === 'out') {
      stock.set(row.article_id, current - quantity);
    } else {
      stock.set(row.article_id, current + quantity);
    }
  }

  return stock;
}

/**
 * Garde les articles qui appartiennent au tenant DES ALERTES, et remonte les
 * tenants étrangers rencontrés.
 *
 * POURQUOI. Les 04 et 05/09/2026, un admin plateforme en « vue client » lisait
 * dans `authorized_articles` les articles du client PLUS ceux de son propre
 * compte Pro (Tunielec) : la vue fusionnait encore la branche Pro de
 * l'opérateur. Le scan a alors calculé un stock nul pour ces 20 articles
 * (aucun mouvement visible) et a inséré 20 alertes « Stock minimum atteint »
 * dans CHAQUE client visité — le trigger `set_tenant_id_from_session` les
 * rattachant au tenant courant. Le serveur est corrigé depuis (migrations
 * 20260905193000 / 20260905195000), mais une alerte ne doit JAMAIS pouvoir
 * décrire l'article d'un autre tenant, quelle que soit la prochaine
 * divergence entre ce que renvoie une lecture et ce que résout une écriture.
 */
export function partitionLowStockCandidatesByTenant<
  T extends { tenant_id: string },
>(rows: T[], tenantId: string): { own: T[]; foreignTenantIds: string[] } {
  const own: T[] = [];
  const foreign = new Set<string>();
  for (const row of rows) {
    if (row.tenant_id === tenantId) own.push(row);
    else foreign.add(row.tenant_id);
  }
  return { own, foreignTenantIds: [...foreign] };
}

/** Le tenant vers lequel le serveur rattacherait une insertion sans
 *  `tenant_id` explicite — même résolveur que le trigger de la table
 *  (`current_user_tenant_id()` : vue admin, puis espace actif, puis première
 *  adhésion). En le lisant AVANT le scan et en le figeant sur chaque ligne,
 *  lecture et écriture parlent forcément du même tenant. */
async function resolveNotificationTenantId(
  supabase: TypedSupabaseClient,
): Promise<string | null> {
  const { data, error } = await (supabase as unknown as RpcClient).rpc(
    'current_user_tenant_id',
    {},
  );
  if (error) throw error;
  return typeof data === 'string' && data.length > 0 ? data : null;
}

export async function ensureLowStockNotificationsForArticles(
  supabase: TypedSupabaseClient,
  articleIds?: string[],
): Promise<number> {
  const tenantId = await resolveNotificationTenantId(supabase);
  // Sans tenant résolu (session anonyme, adhésion révoquée, espace demandé
  // non autorisé), il n'y a personne à alerter — et surtout aucun endroit
  // légitime où écrire.
  if (!tenantId) return 0;

  const scopedArticleIds = [...new Set((articleIds ?? []).filter(Boolean))];

  const scopeChunks: (string[] | undefined)[] =
    scopedArticleIds.length > 0 ? chunkIds(scopedArticleIds) : [undefined];
  const visibleArticles: LowStockArticleRow[] = [];
  for (const scope of scopeChunks) {
    const rows = await fetchAllRows((from, to) => {
      let articleQuery = supabase
        .from('authorized_articles' as 'articles')
        .select('id, code, name, stock_min, unit, tenant_id')
        .eq('is_active', true)
        .gt('stock_min', 0);
      if (scope) {
        articleQuery = articleQuery.in('id', scope);
      }
      return articleQuery.order('id').range(from, to);
    });
    visibleArticles.push(...(rows as LowStockArticleRow[]));
  }

  const { own: candidates, foreignTenantIds } =
    partitionLowStockCandidatesByTenant(visibleArticles, tenantId);
  if (foreignTenantIds.length > 0) {
    console.warn(
      '[low-stock] ignoring articles owned by another tenant',
      { tenantId, foreignTenantIds },
    );
  }
  if (candidates.length === 0) return 0;

  const candidateIds = candidates.map((article) => article.id);
  const movements: StockMovementRow[] = [];
  for (const ids of chunkIds(candidateIds)) {
    const rows = await fetchAllRows((from, to) =>
      supabase
        .from('authorized_stock_movements' as 'stock_movements')
        .select('article_id, movement_type, quantity')
        .eq('tenant_id', tenantId)
        .in('article_id', ids)
        .order('id')
        .range(from, to),
    );
    movements.push(...(rows as StockMovementRow[]));
  }

  const stockByArticle = aggregateStock(movements);
  const lowStockArticles = candidates
    .map((article) => ({
      article,
      currentStock: stockByArticle.get(article.id) ?? 0,
      stockMin: Number(article.stock_min ?? 0),
    }))
    .filter(({ currentStock, stockMin }) => currentStock <= stockMin);

  if (lowStockArticles.length === 0) return 0;

  const lowStockIds = lowStockArticles.map(({ article }) => article.id);
  const alreadyNotified = new Set<string>();
  for (const ids of chunkIds(lowStockIds)) {
    const existing = await fetchAllRows((from, to) =>
      supabase
        .from('admin_notifications')
        .select('entity_id')
        .eq('tenant_id', tenantId)
        .eq('kind', 'product_low_stock')
        .eq('entity_type', 'article')
        .in('entity_id', ids)
        .order('id')
        .range(from, to),
    );
    for (const row of existing) {
      if (row.entity_id) alreadyNotified.add(row.entity_id);
    }
  }
  const rowsToInsert = lowStockArticles
    .filter(({ article }) => !alreadyNotified.has(article.id))
    .map(({ article, currentStock, stockMin }) => {
      const unit = article.unit ?? '';
      const currentText = `${formatQty(currentStock)}${unit ? ` ${unit}` : ''}`;
      const minText = `${formatQty(stockMin)}${unit ? ` ${unit}` : ''}`;
      return {
        // Explicite, et non laissé au trigger : la policy RLS (`WITH CHECK
        // tenant_id = current_user_tenant_id()`) refuse alors toute ligne
        // qui ne serait pas celle du tenant résolu ci-dessus.
        tenant_id: tenantId,
        kind: 'product_low_stock',
        title: `Stock minimum atteint: ${article.name}`,
        message: `Stock actuel: ${currentText}. Minimum: ${minText}.`,
        link_path: `/articles/${article.id}`,
        entity_type: 'article',
        entity_id: article.id,
        metadata: {
          articleId: article.id,
          articleCode: article.code,
          currentStock,
          stockMin,
          unit,
        },
      };
    });

  if (rowsToInsert.length === 0) return 0;

  const { data: inserted, error: insertError } = await supabase
    .from('admin_notifications')
    .insert(rowsToInsert)
    .select('id');
  if (insertError) {
    if (isUniqueViolation(insertError)) return 0;
    if (isRowLevelSecurityViolation(insertError)) {
      // Le serveur a tranché : ces alertes ne sont pas les nôtres. Rien n'a
      // été écrit (insertion atomique) ; on ne remonte pas d'erreur à la
      // cloche, on laisse une trace pour l'investigation.
      console.warn('[low-stock] insert refused by the server', { tenantId, insertError });
      return 0;
    }
    throw insertError;
  }

  return inserted?.length ?? rowsToInsert.length;
}

function errorCode(error: unknown): string | undefined {
  return typeof error === 'object' && error !== null && 'code' in error
    ? (error as { code?: string }).code
    : undefined;
}

function isUniqueViolation(error: unknown): boolean {
  return errorCode(error) === '23505';
}

function isRowLevelSecurityViolation(error: unknown): boolean {
  return errorCode(error) === '42501';
}
